import React, { useState, useEffect } from 'react';
import CountUp from 'react-countup';


const LightweightClientBriefForm = ({ apiBaseUrl = process.env.REACT_APP_API_BASE_URL }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        region: '',
        budget: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [offMarketCount, setOffMarketCount] = useState(null);
    const [propertyExamples, setPropertyExamples] = useState([]);

    useEffect(() => {
        // Fetch summary count
        fetch(`${apiBaseUrl}/public/property/summary-count`)
            .then(res => res.json())
            .then(data => setOffMarketCount(data?.total || 0))
            .catch(err => console.error('Failed to fetch property count:', err));

        // Fetch recent example properties
        fetch(`${apiBaseUrl}/public/property/summary?postedWithin=7`)
            .then(res => res.json())
            .then(data => {
                const samples = Array.isArray(data) ? data.slice(0, 3) : [];
                setPropertyExamples(samples);
            })
            .catch(err => console.error('Failed to load property preview:', err));
    }, [apiBaseUrl]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            clientName: formData.name,
            email: formData.email,
            phoneNumber: formData.phone,
            budget: {
                max: parseFloat(formData.budget) || 0
            },
            preferredLocations: [{
                name: formData.region,
                type: 'region'
            }],
            investmentStrategy: '',
            source: 'npc-quick-brief',

            // ✅ Hardcoded values
            createdBy: '67bc46329fc18be4a429941f',
            buyerAgentId: '67bc46329fc18be4a429941f',

            hasPreApproval: false,
            entityType: 'Not Decided', // make sure this is a valid enum
            weightage: {
                location: 2,
                budget: 2,
                bedrooms: 2,
                bathrooms: 2,
                landSize: 2,
                ageOfProperty: 2,
                maxMonthlyHoldingCost: 2,
                minYield: 2,
                subdivisionPotential: 2,
                renovationPotential: 2,
                propertyType: 2
            },
            includeInScoring: {
                location: true,
                budget: true,
                bedrooms: true,
                bathrooms: true,
                landSize: true,
                ageOfProperty: true,
                maxMonthlyHoldingCost: false,
                minYield: true,
                subdivisionPotential: false,
                renovationPotential: false,
                propertyType: true
            }
        };


        try {
            await fetch(`${apiBaseUrl}/client-briefs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            setSubmitted(true);
        } catch (err) {
            console.error('Brief submission failed:', err);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-md p-6 ring-1 ring-gray-200 max-w-lg mx-auto w-full">
            <h2 className="text-2xl font-bold text-[#003366] mb-4 text-center">
                Get Access to Off-Market Properties
            </h2>

            {offMarketCount > 0 && (
                <div className="flex flex-col items-center justify-center mb-6">
                    <div className="bg-[#003366] text-white w-24 h-24 rounded-full flex items-center justify-center shadow-lg animate-pulse-slow mb-2">
                        <span className="text-2xl font-bold">
                            <CountUp end={offMarketCount} duration={1.5} separator="," />
                        </span>
                    </div>
                    <p className="text-sm text-gray-700 text-center max-w-xs">
                        off-market & pre-market listings Australia-wide
                    </p>
                </div>
            )}



            {propertyExamples.length > 0 && (
                <div className="mb-6 text-sm text-gray-700 text-center">
                    <p className="font-medium text-gray-800 mb-1">Examples of recent listings:</p>
                    <ul className="text-gray-600 space-y-1">
                        {propertyExamples.map((p, i) => (
                            <li key={i}>
                                • {p.bedrooms} bed {p.propertyType?.toLowerCase() || 'property'} in {p.address?.suburb || p.address?.city || 'unknown location'}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {submitted ? (
                <p className="text-green-600 font-semibold text-center">Thanks! We'll follow up with matches soon.</p>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">Full Name</label>
                        <input
                            type="text"
                            name="name"
                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">Email</label>
                        <input
                            type="email"
                            name="email"
                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">Phone</label>
                        <input
                            type="tel"
                            name="phone"
                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">Preferred Region</label>
                        <input
                            type="text"
                            name="region"
                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={formData.region}
                            onChange={handleChange}
                            placeholder="e.g., Western Sydney"
                        />
                    </div>
                    <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">Maximum Budget ($)</label>
                        <input
                            type="number"
                            name="budget"
                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={formData.budget}
                            onChange={handleChange}
                        />
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-[#da3e13] text-white py-2 rounded-lg font-semibold hover:bg-[#b9320f] transition text-center"
                    >
                        Get Matching Offmarket
                    </button>
                </form>
            )}
        </div>
    );
};

export default LightweightClientBriefForm;
